import { cva } from 'class-variance-authority';

/**
 * Skeleton class recipes. The fill lives on `.ds-skeleton` in `design-system.css`, so there is no
 * `dark:` variant here.
 *
 * Ported from Figma `Skeleton`, page `Skeleton [Draft]`, read 2026-09-29: Shape (Text 240x16 with
 * radius/sm, Block 240x120 with radius/md, Circle 40x40 with radius/full). The defaults are the
 * drawn sizes; the caller overrides width and height through `className`, and the shape and radius
 * stay.
 *
 * Motion is the pulse and only the pulse: opacity, never a sweeping gradient, and every skeleton in a
 * region starts on the same frame because they mount together. UNDER REDUCED MOTION IT IS STILL,
 * unlike the reduced-motion spinner: a skeleton's pulse carries no meaning (the region's status text
 * does), so it is decoration, and decoration goes.
 *
 * It fades in on the control clock when it mounts, like the Spinner, so it never pops in. The fade is
 * on the fill, not on opacity, because the pulse already owns opacity and an animation beats a
 * transition on the same property. It is unmounted, not faded out, when the content lands. The fade is
 * kept under reduced motion: it is an appearance, not movement.
 */
export const skeletonRecipe = cva(
  [
    'ds-skeleton block bg-[var(--ds-skeleton-fill)]',
    'transition-[background-color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] starting:bg-transparent',
  ],
  {
    variants: {
      animated: {
        true: 'animate-ds-pulse motion-reduce:animate-none',
        false: '',
      },
      shape: {
        text: 'h-4 w-full rounded-sm',
        // 120 is the drawn height and is not a step on the spacing scale (which stops at 64), so it is
        // written as the value it is rather than as a class the scale guard rightly rejects.
        block: 'h-[120px] w-full rounded-md',
        circle: 'size-10 shrink-0 rounded-full',
      },
    },
    defaultVariants: { shape: 'text', animated: true },
  }
);

/** Several lines of text: bars stacked with x2 (8) between them. */
export const skeletonStackRecipe = cva('flex w-full flex-col gap-2');

/**
 * `MeasuredSkeleton`. The root is the positioning context for the overlay, and carries
 * `.ds-skeleton-measured`, whose rules in `slots.css` hide the sample while loading.
 */
export const measuredSkeletonRootRecipe = cva('ds-skeleton-measured relative min-w-0');

/**
 * The shapes sit in ONE overlay that pulses as a whole, so a region of a hundred bars is one animated
 * layer rather than a hundred. Still under reduced motion, like `Skeleton`. `contain` keeps the
 * overlay's layout and paint from leaking into the page.
 */
export const measuredSkeletonOverlayRecipe = cva(
  'pointer-events-none absolute inset-0 animate-ds-pulse motion-reduce:animate-none [contain:layout_paint_style]'
);

/** One measured shape, placed by the inline geometry the measure returns. */
export const measuredSkeletonShapeRecipe = cva('absolute');
